/**
 * UI Manager Module
 * Quản lý rendering UI (autocomplete, totals, courses list)
 */

import * as StateManager from '../shared/state-manager.js';
import { formatPrice, escapeHtml } from '../shared/utils.js';

// Import dependencies
let addCourseToSelection, getCampaignDiscount, loadPromotionalCampaigns;

export function injectDependencies(deps) {
    addCourseToSelection = deps.addCourseToSelection;
    getCampaignDiscount = deps.getCampaignDiscount;
    loadPromotionalCampaigns = deps.loadPromotionalCampaigns;
}

/**
 * Render cart courses UI
 */
export function renderSelectedCourses() {
    const selectedCourses = StateManager.getSelectedCourses();
    const $cache = StateManager.getCache();

    if (selectedCourses.length === 0) {
        $cache.selectedCourses.html('');
        $cache.emptyState.show();
        $cache.registrationSection.hide();
        $cache.paymentSummarySection.hide();
        return;
    }

    $cache.emptyState.hide();
    $cache.registrationSection.show();
    $cache.paymentSummarySection.show();

    let html = '';

    selectedCourses.forEach((course, index) => {
        const currentPrice = parseFloat(course.final_price) || 0;
        let originalPrice = parseFloat(course.regular_price) || 0;

        // Lấy tên biến thể hiện tại (nếu có)
        let currentVariationLabel = '';
        if (course.selected_variation) {
            originalPrice = parseFloat(course.selected_variation.regular_price) || currentPrice;
            currentVariationLabel = course.selected_variation.formatted_attributes
                ? Object.values(course.selected_variation.formatted_attributes).join(' - ')
                : '';
        }

        const hasDiscount = originalPrice > currentPrice;
        let variationMarkup = '';

        if (course.type === 'variable' && course.variations.length > 0) {
            let options = '';
            let variationsData = {};
            course.variations.forEach(variation => {
                const label = variation.formatted_attributes
                    ? Object.values(variation.formatted_attributes).join(' - ')
                    : (window.ktvhvnCourseReg.i18n.default_option || 'Mặc định');

                const isSelected = course.selected_variation &&
                    parseInt(course.selected_variation.variation_id, 10) === parseInt(variation.variation_id, 10);

                options += `
                    <option value="${variation.variation_id}" ${isSelected ? 'selected' : ''}>
                        ${escapeHtml(label)}
                    </option>
                `;

                // Store variation description for popup
                variationsData[variation.variation_id] = variation.description || '';
            });

            // Get current selected variation description
            const currentDescription = course.selected_variation && course.selected_variation.description
                ? course.selected_variation.description
                : '';

            const hasDescription = currentDescription.trim() !== '';

            variationMarkup = `
                <div class="ktvhvn-cart-variation">
                    <div class="ktvhvn-variation-wrapper">
                        <select class="ktvhvn-variation-select" data-index="${index}" data-variations='${JSON.stringify(variationsData).replace(/'/g, "&#39;")}'>
                            ${options}
                        </select>
                        ${hasDescription ? `
                            <button type="button" class="ktvhvn-variation-info-btn" data-index="${index}" data-description="${escapeHtml(currentDescription).replace(/"/g, '&quot;')}" title="Xem mô tả">
                                <i class="fa fa-question-circle"></i>
                            </button>
                        ` : ''}
                    </div>
                    ${hasDescription ? `
                        <div class="ktvhvn-variation-description" data-index="${index}">
                            ${currentDescription}
                        </div>
                    ` : ''}
                </div>
            `;
        } else {
            variationMarkup = `
                <div class="ktvhvn-cart-variation ktvhvn-cart-variation--static">
                    <span>${window.ktvhvnCourseReg.i18n.default_option || 'Mặc định'}</span>
                </div>
            `;
        }

        html += `
            <div class="ktvhvn-cart-item">
                <div class="ktvhvn-cart-item-head">
                    <p class="course-title">
                        <a href="${course.permalink || '#'}" target="_blank" rel="noopener noreferrer">${escapeHtml(course.name)}</a>
                    </p>
                    <button type="button" class="ktvhvn-remove-course" data-index="${index}">
                        <i class="fa fa-trash"></i> ${window.ktvhvnCourseReg.i18n.remove || 'Xóa'}
                    </button>
                </div>
                <div class="ktvhvn-cart-item-body">
                    ${variationMarkup}
                    <div class="ktvhvn-cart-item-price">
                        ${hasDiscount ? `<span class="price-old">${formatPrice(originalPrice, currentVariationLabel)}</span>` : ''}
                        <span class="price-current">${formatPrice(currentPrice, currentVariationLabel)}</span>
                    </div>
                </div>
            </div>
        `;
    });

    $cache.selectedCourses.html(html);

    // Trigger event for questionnaire addon to load questionnaire for pre-selected variations
    jQuery(document).trigger('ktvhvn_courses_rendered');
}

/**
 * Display totals with discount
 */
export function displayTotalsWithDiscount(total, discountAmount, campaignName) {
    const $cache = StateManager.getCache();
    const finalTotal = total - discountAmount;

    // Update pricing breakdown using cached elements
    // Sử dụng .html() thay vì .text() để render HTML cho "Liên Hệ"
    $cache.subtotalAmount.html(formatPrice(total));

    if (discountAmount > 0) {
        $cache.discountRow.css('display', 'flex');
        $cache.discountAmount.html('-' + formatPrice(discountAmount));

        if (campaignName) {
            $cache.discountCampaignName.text('(' + campaignName + ')');
        } else {
            $cache.discountCampaignName.text('');
        }
    } else {
        $cache.discountRow.hide();
    }

    $cache.totalAmount.html(formatPrice(finalTotal));
}

/**
 * Estimate discount optimistically (instant, no API call)
 * IMPORTANT: This function provides instant feedback while API is loading.
 * When item count changes, we need to recalculate discount based on NEW orderTotal.
 */
export function estimateOptimisticDiscount(orderTotal, freeCoursesDiscount = 0, paidCoursesCount = 0, freeCoursesCount = 0) {
    const campaignsCache = StateManager.getCampaignsCache();
    let selectedCampaignId = StateManager.getSelectedCampaignId();
    const discountPercent = StateManager.getDiscountPercent();
    const selectedCourses = StateManager.getSelectedCourses();
    const currentItemCount = selectedCourses.length;
    const cachedItemCount = StateManager.getCurrentCampaignsItemCount();

    // ✅ FREE COURSES: Calculate total discount including free courses + campaign additional discount
    if (freeCoursesCount > 0 && freeCoursesDiscount > 0) {
        let totalDiscount = freeCoursesDiscount;
        let campaignName = 'Khóa học miễn phí';

        // Check if there's a selected campaign with additional discount
        if (selectedCampaignId && campaignsCache && campaignsCache.campaigns) {
            const selectedCampaign = campaignsCache.campaigns.find(c => c.id === selectedCampaignId);
            if (selectedCampaign) {
                campaignName = selectedCampaign.name;

                // If campaign is free_item type with additional percentage discount
                if (selectedCampaign.discount_type === 'free_item' && parseFloat(selectedCampaign.discount_value) > 0) {
                    const additionalPercent = parseFloat(selectedCampaign.discount_value);
                    const paidTotal = orderTotal - freeCoursesDiscount;
                    const additionalDiscount = paidTotal * additionalPercent / 100;
                    totalDiscount += additionalDiscount;
                }
            }
        } else if (paidCoursesCount === 1 && freeCoursesCount === 1) {
            campaignName = 'Mua 1 Tặng 1';
        }

        return {
            amount: totalDiscount,
            name: campaignName
        };
    }

    // Check if item count has changed - if so, we need to recalculate
    const itemCountChanged = currentItemCount !== cachedItemCount;

    // FIX: Check if currently selected campaign is still applicable based on current item count
    if (selectedCampaignId && campaignsCache && campaignsCache.campaigns) {
        const selectedCampaign = campaignsCache.campaigns.find(c => c.id === selectedCampaignId);
        if (selectedCampaign) {
            const exactItems = parseInt(selectedCampaign.exact_items) || 0;
            const minItems = parseInt(selectedCampaign.min_items) || 0;

            // Check if campaign is still applicable
            let isApplicable = true;
            if (exactItems > 0 && currentItemCount !== exactItems) {
                isApplicable = false;
            }
            if (minItems > 0 && currentItemCount < minItems) {
                isApplicable = false;
            }

            if (!isApplicable) {
                // Campaign no longer applicable - reset
                StateManager.setSelectedCampaignId(null);
                selectedCampaignId = null;
            }
        }
    }

    // Find best applicable campaign from cache based on current item count
    let bestApplicableCampaign = null;
    if (campaignsCache && campaignsCache.campaigns) {
        const applicableCampaigns = campaignsCache.campaigns.filter(c => {
            const cExact = parseInt(c.exact_items) || 0;
            const cMin = parseInt(c.min_items) || 0;
            const cMinOrder = parseFloat(c.min_order_value) || 0;
            if (cExact > 0 && currentItemCount !== cExact) return false;
            if (cMin > 0 && currentItemCount < cMin) return false;
            if (cMinOrder > 0 && orderTotal < cMinOrder) return false;
            return true;
        });

        if (applicableCampaigns.length > 0) {
            // Calculate estimated discount for each campaign based on CURRENT orderTotal
            const campaignsWithEstimate = applicableCampaigns.map(c => {
                let estimatedDiscount = 0;
                const discountType = c.discount_type;
                const discountValue = parseFloat(c.discount_value) || 0;

                if (discountType === 'percentage') {
                    estimatedDiscount = orderTotal * discountValue / 100;
                } else if (discountType === 'fixed_amount') {
                    estimatedDiscount = Math.min(discountValue, orderTotal);
                } else if (discountType === 'free_item') {
                    // free_item: discount_value is additional % off
                    estimatedDiscount = orderTotal * discountValue / 100;
                }

                return { ...c, estimatedDiscount };
            });

            // Find campaign with highest estimated discount
            bestApplicableCampaign = campaignsWithEstimate.reduce((best, current) => {
                return current.estimatedDiscount > best.estimatedDiscount ? current : best;
            }, campaignsWithEstimate[0]);
        }
    }

    // Auto-select best campaign if none selected
    if (!selectedCampaignId && bestApplicableCampaign) {
        StateManager.setSelectedCampaignId(bestApplicableCampaign.id);
        selectedCampaignId = bestApplicableCampaign.id;
    }

    // If we have a selected campaign, calculate discount based on CURRENT orderTotal
    if (selectedCampaignId && campaignsCache && campaignsCache.campaigns) {
        const selectedCampaign = campaignsCache.campaigns.find(c => c.id === selectedCampaignId);
        if (selectedCampaign) {
            const discountType = selectedCampaign.discount_type;
            const discountValue = parseFloat(selectedCampaign.discount_value) || 0;

            let estimatedDiscount = 0;
            if (discountType === 'percentage') {
                estimatedDiscount = orderTotal * discountValue / 100;
            } else if (discountType === 'fixed_amount') {
                estimatedDiscount = Math.min(discountValue, orderTotal);
            } else if (discountType === 'free_item') {
                // free_item: discount_value is additional % off
                estimatedDiscount = orderTotal * discountValue / 100;
            }

            // If item count hasn't changed, use cached discount_amount (more accurate)
            if (!itemCountChanged && selectedCampaign.discount_amount) {
                estimatedDiscount = parseFloat(selectedCampaign.discount_amount) || estimatedDiscount;
            }

            return {
                amount: estimatedDiscount,
                name: selectedCampaign.name
            };
        }
    }

    // Fallback to bulk discount (5+ courses)
    if (discountPercent > 0) {
        return {
            amount: orderTotal * (discountPercent / 100),
            name: 'Giảm ' + discountPercent + '% cho đơn từ 5 khóa'
        };
    }

    return { amount: 0, name: '' };
}

/**
 * Totals block update
 */
export function updateTotals() {
    const selectedCourses = StateManager.getSelectedCourses();
    const totalCourses = selectedCourses.length;
    const discountPercent = totalCourses >= 5 ? 12 : 0;
    StateManager.setDiscountPercent(discountPercent);

    let total = 0;
    let freeCoursesDiscount = 0;
    let paidCoursesCount = 0;
    let freeCoursesCount = 0;

    selectedCourses.forEach(course => {
        const price = parseFloat(course.final_price) || 0;
        total += price;

        // ✅ FREE COURSES: Track free courses discount
        if (course.isFreeCourse) {
            freeCoursesDiscount += price;
            freeCoursesCount++;
        } else {
            paidCoursesCount++;
        }
    });

    // Save free courses discount to state
    StateManager.setFreeCoursesDiscount(freeCoursesDiscount);

    // Include questionnaire/add-on price (from questionnaire addon)
    const questionnairePrice = parseFloat(window.ktvhvnQuestionnairePrice || 0) || 0;
    total += questionnairePrice;

    // ✅ AUTO-SELECT "Mua 1 Tặng 1": When exactly 1 paid + 1 free course
    if (paidCoursesCount === 1 && freeCoursesCount === 1) {
        const campaignsCache = StateManager.getCampaignsCache();
        if (campaignsCache && campaignsCache.campaigns) {
            const mua1Tang1 = campaignsCache.campaigns.find(c => c.id === 'mua-1-tang-1');
            if (mua1Tang1) {
                StateManager.setSelectedCampaignId('mua-1-tang-1');
            }
        }
    }

    // STEP 1: INSTANT PREVIEW - Show optimistic discount immediately
    const optimisticDiscount = estimateOptimisticDiscount(total, freeCoursesDiscount, paidCoursesCount, freeCoursesCount);
    displayTotalsWithDiscount(total, optimisticDiscount.amount, optimisticDiscount.name);

    // STEP 2: ACCURATE VALIDATION - Load campaigns from API (debounced 500ms)
    loadPromotionalCampaigns(total);
}

// Module-level state for dropdown
let dropdownUpdateFn = null;
let $dropdownEl = null;

/**
 * Initialize custom autocomplete for course selection
 */
export function initializeCustomAutocomplete() {
    const $select = jQuery('#ktvhvn-course-select');
    const $wrapper = $select.parent();

    // Hide the original select
    $select.hide();

    // Create wrapper for input
    const $inputWrapper = jQuery('<div>', {
        class: 'ktvhvn-autocomplete-wrapper'
    });

    // Create custom autocomplete input
    const $input = jQuery('<input>', {
        type: 'text',
        class: 'ktvhvn-course-autocomplete',
        placeholder: 'Nhập tên khóa học để tìm kiếm...',
        autocomplete: 'off'
    });

    // Create dropdown container
    const $dropdown = jQuery('<div>', {
        class: 'ktvhvn-autocomplete-dropdown',
        style: 'display: none;'
    });

    // Store reference for external access
    $dropdownEl = $dropdown;

    // Build structure
    $inputWrapper.append($input);
    $select.after($inputWrapper).after($dropdown);

    let isDropdownOpen = false;

    // Show dropdown on focus
    $input.on('focus', function() {
        updateDropdownResults('');
        $dropdown.show();
        isDropdownOpen = true;
    });

    // Filter on input
    $input.on('input', function() {
        const searchTerm = jQuery(this).val();
        updateDropdownResults(searchTerm);
        if (!isDropdownOpen) {
            $dropdown.show();
            isDropdownOpen = true;
        }
    });

    // Close dropdown when clicking outside
    jQuery(document).on('click.ktvhvnAutocomplete', function(e) {
        if (!jQuery(e.target).closest('.ktvhvn-autocomplete-wrapper, .ktvhvn-autocomplete-dropdown').length) {
            $dropdown.hide();
            isDropdownOpen = false;
            $input.val('');
        }
    });

    // Update dropdown results based on search term
    function updateDropdownResults(searchTerm) {
        const coursesData = StateManager.getCoursesData();
        const selectedCourses = StateManager.getSelectedCourses();
        const term = searchTerm.toLowerCase().trim();
        let html = '';
        let hasResults = false;

        coursesData.forEach(course => {
            // Skip if already selected
            if (selectedCourses.find(c => c.product_id === course.id)) {
                return;
            }

            // Filter by search term
            if (term && !course.name.toLowerCase().includes(term)) {
                return;
            }

            hasResults = true;
            html += `
                <div class="ktvhvn-autocomplete-item" data-course-id="${course.id}">
                    <span class="course-name">${escapeHtml(course.name)}</span>
                </div>
            `;
        });

        if (!hasResults) {
            html = '<div class="ktvhvn-autocomplete-empty">Không tìm thấy khóa học</div>';
        }

        $dropdown.html(html);
    }

    // Store update function for external access
    dropdownUpdateFn = updateDropdownResults;

    // Handle item selection
    jQuery(document).on('click', '.ktvhvn-autocomplete-item', function() {
        const courseId = parseInt(jQuery(this).data('course-id'), 10);
        if (courseId) {
            // Close dropdown BEFORE adding course to prevent re-open
            $dropdown.hide();
            isDropdownOpen = false;
            $input.val('');
            addCourseToSelection(courseId);
        }
    });
}

/**
 * Refresh dropdown to update hidden options (without showing it)
 * Called after adding/removing courses to update the available options list
 */
export function refreshDropdown() {
    // Only update dropdown content if it exists and is visible
    // Do NOT show dropdown - just silently refresh data for next open
    if (dropdownUpdateFn && $dropdownEl && $dropdownEl.is(':visible')) {
        const $input = jQuery('.ktvhvn-course-autocomplete');
        const searchTerm = $input.length ? $input.val() : '';
        dropdownUpdateFn(searchTerm);
    }
    // If dropdown is hidden, no need to refresh - it will refresh on next focus/input
}
